import { Box, Chip, Collapse, Divider, IconButton, Menu, MenuItem, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MobileNavDrawer from "../SideNav/MobileNavDrawer";
import BecomePartnerLink from "./BecomePartnerLink";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Toolbar from "@mui/material/Toolbar";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import EventSeatOutlinedIcon from "@mui/icons-material/EventSeatOutlined";
import React, { useContext, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import useStyle from "./styles";
import Logo from "../../assets/images/logo.png";
import EmbedContext from "../../context/Embed";
import TopBar from "./TopBar";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS } from "../../utils/designTokens";

const initialsOf = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length || parts[0] === "...") return "";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

/** Avatar + name + reward points; opens the account menu. */
function AccountButton({ name, points, expanded, showDetails, onClick }) {
  const initials = initialsOf(name);
  const hasPoints = typeof points === "number";
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-controls="user-menu"
      aria-haspopup="true"
      aria-expanded={expanded}
      aria-label={`Account${name ? `: ${name}` : ""}${hasPoints ? `, ${points.toLocaleString()} reward points` : ""}`}
      sx={{
        ...BUTTON_RESET,
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        height: 44,
        pl: "4px",
        pr: showDetails ? 1.5 : "4px",
        borderRadius: RADIUS.pill,
        flexShrink: 0,
        transition: "background-color .15s ease",
        "&:hover": { bgcolor: C.surface2 },
        ...FOCUS_RING,
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: C.surface2,
          border: `1px solid ${C.borderStrong}`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT_BODY,
          fontWeight: 700,
          fontSize: 13,
          color: C.ink,
        }}
      >
        {initials || <PersonOutlineIcon sx={{ fontSize: 20 }} />}
      </Box>
      {showDetails && (
        <Box component="span" aria-hidden="true" sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15, minWidth: 0 }}>
          <Box component="span" sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: C.ink, maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </Box>
          {hasPoints && (
            <Box component="span" sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, color: C.gold, fontVariantNumeric: "tabular-nums" }}>
              {`${points.toLocaleString()} pts`}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function DHeader({
  navitems,
  close,
  open,
  anchor,
  name,
  cartCount = 0,
  // Where the bag goes: checkout, or the picker when several carts exist.
  cartHref = "/checkout",
  mobile,
  showFullHeader = true,
  loyaltyVouchers = [],
  // Reward points balance; undefined until it loads (or when signed out).
  points,
}) {
  const classes = useStyle();
  // Embed mode (partner iframe pinned to one restaurant): suppress every
  // link that could navigate away from the pinned restaurant's order flow.
  const { isEmbed } = useContext(EmbedContext);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  // Mobile-only nav drawer (hamburger menu). SideNav is hidden < md and the
  // header has no other nav controls, so without this the customer has no
  // way to reach Restaurants / Deals / Orders / etc. on a phone.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Room for the full "Become a partner" label next to the search bar.
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("lg"));
  const [myRewardsOpen, setMyRewardsOpen] = useState(false);

  // Simple header for home page
  if (!showFullHeader) {
    return (
      <AppBar
        elevation={0}
        position="fixed"
        className={classes.root}
      >
        <Toolbar className={classes.toolbar}>
          {/* Hamburger — mobile only; opens the nav drawer that mirrors SideNav. */}
          {mobile && !isEmbed && (
            <IconButton
              onClick={() => setMobileNavOpen(true)}
              sx={{ mr: 0.5, color: "#0f172a" }}
              aria-label="Open menu"
            >
              <MenuRoundedIcon />
            </IconButton>
          )}
          {/* Logo */}
          {isEmbed ? (
            <img
              src={Logo}
              alt="RushmyFood"
              style={{
                height: 40,
                objectFit: "contain",
              }}
            />
          ) : (
          <RouterLink
            to="/restaurant-list"
            className={classes.linkDecoration}
          >
            <img
              src={Logo}
              alt="RushmyFood"
              style={{
                height: 40,
                objectFit: "contain",
              }}
            />
          </RouterLink>
          )}

          {/* Right Side Actions */}
          <Box className={classes.rightSection}>
            <BecomePartnerLink compact={mobile} />
            {/* User Menu */}
            <Button
              aria-controls="user-menu"
              aria-haspopup="true"
              onClick={(event) => open(event)}
              sx={{
                borderRadius: "24px",
                backgroundColor: "transparent",
                padding: "8px 16px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              <PersonOutlineIcon sx={{ color: "#191919", fontSize: 22 }} />
              {!mobile && name && (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    ml: 1,
                    fontWeight: 500,
                    color: "#191919",
                    maxWidth: 80,
                  }}
                >
                  {name}
                </Typography>
              )}
              {anchor === null ? (
                <ExpandMoreIcon sx={{ color: "#191919", fontSize: 20, ml: 0.5 }} />
              ) : (
                <ExpandLessIcon sx={{ color: "#191919", fontSize: 20, ml: 0.5 }} />
              )}
            </Button>

            <Menu
              id="user-menu"
              anchorEl={anchor}
              open={Boolean(anchor)}
              onClose={close}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                  marginTop: "8px",
                  minWidth: 180,
                },
              }}
            >
              {(isEmbed
                ? navitems.filter(
                    (item) => item.link === "/orders" || item.link === "/login"
                  )
                : navitems
              ).map((item, index) => (
                <RouterLink
                  key={index}
                  to={item.link}
                  className={classes.linkDecoration}
                  style={{ width: "100%" }}
                >
                  <MenuItem
                    className={classes.menuItem}
                    onClick={() => close(item.title)}
                  >
                    <Typography
                      sx={{
                        color: "#191919",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      {item.title}
                    </Typography>
                  </MenuItem>
                </RouterLink>
              ))}
            </Menu>

            {/* Cart */}
            {cartCount > 0 ? (
              <RouterLink to={cartHref} className={classes.linkDecoration}>
                <Box className={classes.cartButton}>
                  <ShoppingBagOutlinedIcon sx={{ fontSize: 20 }} />
                  <Typography className={classes.cartText}>
                    {cartCount}
                  </Typography>
                </Box>
              </RouterLink>
            ) : (
              <RouterLink to={cartHref}>
                <Box className={classes.iconButton}>
                  <ShoppingBagOutlinedIcon sx={{ color: "#191919", fontSize: 22 }} />
                </Box>
              </RouterLink>
            )}
          </Box>
        </Toolbar>
        {mobile && !isEmbed && (
          <MobileNavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
        )}
      </AppBar>
    );
  }

  // Full header: the shared top bar, with the avatar + account menu on the right.
  const accountControl = (
    <>
      <AccountButton
        name={name}
        points={points}
        expanded={Boolean(anchor)}
        showDetails={wide}
        onClick={(event) => open(event)}
      />
      <Menu
        id="user-menu"
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
            marginTop: "8px",
            minWidth: 220,
          },
        }}
      >
        {/* Favorites */}
        {!isEmbed && (
        <RouterLink
          to="/favourite"
          className={classes.linkDecoration}
          style={{ width: "100%" }}
        >
          <MenuItem className={classes.menuItem} onClick={() => close()}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FavoriteBorderIcon sx={{ fontSize: 18, color: "#191919" }} />
              <Typography sx={{ color: "#191919", fontSize: "0.875rem", fontWeight: 500 }}>
                Favorites
              </Typography>
            </Box>
          </MenuItem>
        </RouterLink>
        )}

        {/* My Orders — kept in embed mode so returning customers can find their orders */}
        <RouterLink to="/orders" className={classes.linkDecoration} style={{ width: "100%" }}>
          <MenuItem className={classes.menuItem} onClick={() => close()}>
            <Typography sx={{ color: "#191919", fontSize: "0.875rem", fontWeight: 500 }}>
              My Orders
            </Typography>
          </MenuItem>
        </RouterLink>

        {/* Support Tickets */}
        {!isEmbed && (
        <RouterLink to="/my-tickets" className={classes.linkDecoration} style={{ width: "100%" }}>
          <MenuItem className={classes.menuItem} onClick={() => close()}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SupportAgentOutlinedIcon sx={{ fontSize: 18, color: "#191919" }} />
              <Typography sx={{ color: "#191919", fontSize: "0.875rem", fontWeight: 500 }}>
                Support Tickets
              </Typography>
            </Box>
          </MenuItem>
        </RouterLink>
        )}

        {/* Table reservations */}
        {!isEmbed && (
        <RouterLink to="/reservations" className={classes.linkDecoration} style={{ width: "100%" }}>
          <MenuItem className={classes.menuItem} onClick={() => close()}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventSeatOutlinedIcon sx={{ fontSize: 18, color: "#191919" }} />
              <Typography sx={{ color: "#191919", fontSize: "0.875rem", fontWeight: 500 }}>
                My Reservations
              </Typography>
            </Box>
          </MenuItem>
        </RouterLink>
        )}

        {/* Wallet */}
        {!isEmbed && (
        <RouterLink to="/wallet" className={classes.linkDecoration} style={{ width: "100%" }}>
          <MenuItem className={classes.menuItem} onClick={() => close()}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18, color: "#191919" }} />
              <Typography sx={{ color: "#191919", fontSize: "0.875rem", fontWeight: 500 }}>
                Wallet
              </Typography>
            </Box>
          </MenuItem>
        </RouterLink>
        )}

        {/* Profile */}
        {!isEmbed && (
        <RouterLink to="/profile" className={classes.linkDecoration} style={{ width: "100%" }}>
          <MenuItem className={classes.menuItem} onClick={() => close()}>
            <Typography sx={{ color: "#191919", fontSize: "0.875rem", fontWeight: 500 }}>
              Profile
            </Typography>
          </MenuItem>
        </RouterLink>
        )}

        {/* My Rewards — label navigates to /rewards, chevron toggles sub-items */}
        {!isEmbed && (
        <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 0.5 }}>
          <RouterLink
            to="/rewards"
            className={classes.linkDecoration}
            style={{ flex: 1 }}
            onClick={() => close()}
          >
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1,
              py: 0.75, borderRadius: 1,
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }, px: 0.5,
            }}>
              <StarOutlinedIcon sx={{ fontSize: 18, color: "#CC0000" }} />
              <Typography sx={{ color: "#191919", fontSize: "0.875rem", fontWeight: 600 }}>
                My Rewards
              </Typography>
            </Box>
          </RouterLink>
          <Box
            onClick={() => setMyRewardsOpen((prev) => !prev)}
            sx={{
              cursor: "pointer", p: 0.75, borderRadius: 1,
              "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
              display: "flex", alignItems: "center",
            }}
          >
            {myRewardsOpen
              ? <ExpandLessIcon sx={{ fontSize: 18, color: "#6b7280" }} />
              : <ExpandMoreIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            }
          </Box>
        </Box>
        )}

        {!isEmbed && (
        <Collapse in={myRewardsOpen} timeout="auto" unmountOnExit>
          {/* My Coupons sub-item */}
          <RouterLink to="/my-coupons" className={classes.linkDecoration} style={{ width: "100%" }}>
            <MenuItem className={classes.menuItem} onClick={() => close()} sx={{ pl: 4.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CardGiftcardOutlinedIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
                <Typography sx={{ color: "#374151", fontSize: "0.85rem", fontWeight: 500 }}>
                  My Coupons
                </Typography>
              </Box>
            </MenuItem>
          </RouterLink>

          {/* Promotional Coupons sub-item — direct link to page */}
          <RouterLink to="/promotional-coupons" className={classes.linkDecoration} style={{ width: "100%" }}>
            <MenuItem className={classes.menuItem} onClick={() => close()} sx={{ pl: 4.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CardGiftcardOutlinedIcon sx={{ fontSize: 16, color: "#d97706" }} />
                <Typography sx={{ color: "#374151", fontSize: "0.85rem", fontWeight: 500 }}>
                  Promotional Coupons
                </Typography>
                {loyaltyVouchers.length > 0 && (
                  <Chip
                    label={loyaltyVouchers.length}
                    size="small"
                    sx={{
                      height: 18, fontSize: "0.7rem", fontWeight: 700,
                      bgcolor: "#fef3c7", color: "#92400e", border: "1px solid #fbbf24",
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          </RouterLink>
        </Collapse>
        )}

        {!isEmbed && <Divider sx={{ my: 0.5 }} />}

        {/* Settings & Logout from navitems (respects translations + logout handler) */}
        {navitems
          .filter((item) => item.link === "/settings" || item.link === "/login")
          .map((item, index) => (
            <RouterLink
              key={index}
              to={item.link}
              className={classes.linkDecoration}
              style={{ width: "100%" }}
            >
              <MenuItem className={classes.menuItem} onClick={() => close(item.title)}>
                <Typography
                  sx={{
                    color: item.link === "/login" ? "#ef4444" : "#191919",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {item.title}
                </Typography>
              </MenuItem>
            </RouterLink>
          ))}
      </Menu>
    </>
  );

  return (
    <TopBar
      account={accountControl}
      cartCount={cartCount}
      cartHref={cartHref}
      showNav
    />
  );
}

export default React.memo(DHeader);
